import { useEffect, useState, useCallback, useRef } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { useTabNav } from "@/context/TabNavContext";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import ListEditor from "@/components/ListEditor";
import ReportCard from "@/components/ReportCard";
import { DeptPicker } from "@/components/DeptPicker";
import PlanTracker from "@/components/PlanTracker";
import Leaderboard from "@/components/Leaderboard";
import { toast } from "sonner";
import { Loader2, Send, CalendarDays, History, Plus, X, ListChecks, Trophy, Coffee, Plane, ClipboardList, FileEdit, Check, Wrench } from "lucide-react";
import MomView from "@/pages/MomView";
import MyTodosView from "@/pages/MyTodosView";
import LeaveView from "@/pages/LeaveView";
import TicketsView from "@/pages/TicketsView";

const today = () => new Date().toISOString().slice(0, 10);
const yesterday = () => { const d = new Date(); d.setDate(d.getDate() - 1); return d.toISOString().slice(0, 10); };
const fmtTs = (iso) => {
  try { return new Date(iso).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }); }
  catch { return ""; }
};
const moods = [
  { v: 1, e: "😟" },
  { v: 2, e: "😐" },
  { v: 3, e: "🙂" },
  { v: 4, e: "😀" },
  { v: 5, e: "🚀" },
];

function PlanEditor({ items, onChange }) {
  const [value, setValue] = useState("");
  const today = new Date().toISOString().slice(0, 10);
  const add = () => {
    const v = value.trim();
    if (!v) return;
    const id = (crypto.randomUUID && crypto.randomUUID()) || String(Date.now());
    onChange([...items, { id, text: v, done: false, remark: "", origin_date: today }]);
    setValue("");
  };
  const remove = (id) => onChange(items.filter((i) => i.id !== id));
  const setRemark = (id, remark) => onChange(items.map((i) => (i.id === id ? { ...i, remark } : i)));
  return (
    <div className="space-y-2">
      <div className="flex gap-2">
        <Input
          value={value}
          onChange={(e) => setValue(e.target.value)}
          onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); add(); } }}
          placeholder="What's next? Add a plan for tomorrow"
          data-testid="report-plan-input"
        />
        <Button type="button" variant="secondary" size="icon" className="shrink-0 rounded-lg"
          onClick={add} data-testid="report-plan-add-btn">
          <Plus className="h-4 w-4" />
        </Button>
      </div>
      {items.length > 0 && (
        <ul className="space-y-1.5" data-testid="report-plan-list">
          {items.map((item) => (
            <li key={item.id} className="flex flex-col gap-1.5 text-sm bg-secondary/60 rounded-lg px-3 py-2 group">
              <div className="flex items-start gap-2">
                <span className="mt-1.5 h-1.5 w-1.5 rounded-full shrink-0" style={{ backgroundColor: "hsl(var(--primary))" }} />
                <span className={`flex-1 break-words ${item.done ? "line-through text-muted-foreground" : ""}`}>
                  {item.text}
                  {item.origin_date && item.origin_date !== today && (
                    <span className="ml-2 text-[10px] font-medium text-amber-700 bg-amber-500/15 rounded-full px-1.5 py-0.5" data-testid="plan-origin-chip">since {item.origin_date}</span>
                  )}
                </span>
                <button type="button" onClick={() => remove(item.id)}
                  className="opacity-0 group-hover:opacity-100 transition-opacity text-muted-foreground hover:text-destructive">
                  <X className="h-4 w-4" />
                </button>
              </div>
              <Input value={item.remark || ""} onChange={(e) => setRemark(item.id, e.target.value)}
                placeholder="Remark (optional)" className="h-8 text-xs" data-testid={`plan-remark-${item.id}`} />
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default function MemberView() {
  const { requestedTab, clearTab } = useTabNav();
  const [tab, setTab] = useState("submit");
  useEffect(() => { if (requestedTab) { setTab(requestedTab); clearTab(); } }, [requestedTab, clearTab]);
  const [date, setDate] = useState(today());
  const [summary, setSummary] = useState("");
  const [tasks, setTasks] = useState([]);
  const [plan, setPlan] = useState([]);
  const [mood, setMood] = useState(null);
  const [status, setStatus] = useState("report");
  const [entryState, setEntryState] = useState("new"); // new | draft | submitted
  const [submittedAt, setSubmittedAt] = useState(null);
  const [autoState, setAutoState] = useState("idle"); // idle | saving | saved
  const lastSavedRef = useRef("");
  const [saving, setSaving] = useState(false);
  const [reports, setReports] = useState([]);
  const [loadingHistory, setLoadingHistory] = useState(true);
  const [histStart, setHistStart] = useState("");
  const [histEnd, setHistEnd] = useState("");
  const [depts, setDepts] = useState([]);

  const loadHistory = useCallback(async () => {
    setLoadingHistory(true);
    try {
      const { data } = await api.get("/reports/me");
      setReports(data);
    } catch (e) {
      console.error("Could not load reports", e);
      toast.error("Could not load your reports");
    } finally {
      setLoadingHistory(false);
    }
  }, []);

  const snapshot = useCallback(
    () => JSON.stringify({ summary, tasks, plan, mood, depts }),
    [summary, tasks, plan, mood, depts]
  );

  const loadForDate = useCallback(async (d) => {
    try {
      const { data } = await api.get("/reports/me", { params: { date: d } });
      if (data.length > 0) {
        const r = data[0];
        const s = r.summary || "", tk = r.tasks_done || [], pl = r.plan_tomorrow || [], md = r.mood || null;
        const dp = r.departments || [];
        setSummary(s); setTasks(tk); setPlan(pl); setMood(md); setDepts(dp);
        setStatus(r.status || "report");
        setEntryState(r.is_draft ? "draft" : "submitted");
        setSubmittedAt(r.submitted_at || null);
        setAutoState("idle");
        lastSavedRef.current = JSON.stringify({ summary: s, tasks: tk, plan: pl, mood: md, depts: dp });
      } else {
        setSummary(""); setTasks([]); setMood(null); setStatus("report"); setDepts([]);
        setEntryState("new"); setSubmittedAt(null); setAutoState("idle");
        let planItems = [];
        if (d === today()) {
          try {
            const co = await api.get("/reports/carryover");
            planItems = (co.data.items || []).map((it) => ({
              id: (crypto.randomUUID && crypto.randomUUID()) || String(Math.random()),
              text: it.text,
              done: false,
              remark: it.remark || "",
              origin_date: it.origin_date || co.data.from_date,
            }));
            if (planItems.length) {
              toast(`Carried over ${planItems.length} unfinished plan${planItems.length > 1 ? "s" : ""} from ${co.data.from_date}`);
            }
          } catch (err) {
            planItems = [];
          }
        }
        setPlan(planItems);
        lastSavedRef.current = JSON.stringify({ summary: "", tasks: [], plan: planItems, mood: null, depts: [] });
      }
    } catch (e) {
      console.error("Failed to load report for date", d, e);
    }
  }, []);

  useEffect(() => { loadHistory(); }, [loadHistory]);
  useEffect(() => { loadForDate(date); }, [date, loadForDate]);

  const autoSaveDraft = useCallback(async () => {
    setAutoState("saving");
    try {
      await api.post("/reports", { date, summary, tasks_done: tasks, plan_tomorrow: plan, mood, status: "report", is_draft: true, departments: depts });
      lastSavedRef.current = JSON.stringify({ summary, tasks, plan, mood, depts });
      setEntryState("draft");
      setAutoState("saved");
      loadHistory();
    } catch (err) {
      setAutoState("idle");
    }
  }, [date, summary, tasks, plan, mood, depts, loadHistory]);

  // Auto-save drafts while typing (working-day, not-yet-submitted entries only).
  useEffect(() => {
    if (status !== "report" || entryState === "submitted" || saving) return;
    if (snapshot() === lastSavedRef.current) return;
    const hasContent = summary.trim() || tasks.length || plan.length || mood;
    if (!hasContent) return;
    setAutoState((s) => (s === "saved" ? "idle" : s));
    const t = setTimeout(() => { autoSaveDraft(); }, 1500);
    return () => clearTimeout(t);
  }, [summary, tasks, plan, mood, status, entryState, saving, snapshot, autoSaveDraft]);

  const submit = async (e, asDraft = false) => {
    if (e && e.preventDefault) e.preventDefault();
    setSaving(true);
    try {
      const draft = asDraft && status === "report";
      const { data: saved } = await api.post("/reports", { date, summary, tasks_done: tasks, plan_tomorrow: plan, mood, status, is_draft: draft, departments: depts });
      lastSavedRef.current = JSON.stringify({ summary, tasks, plan, mood, depts });
      setEntryState(draft ? "draft" : "submitted");
      setSubmittedAt(saved?.submitted_at || null);
      setAutoState("idle");
      toast.success(
        status === "off" ? "Marked as Off"
          : status === "leave" ? "Marked as Leave"
          : draft ? "Draft saved — submit it when you're done"
          : "Report submitted!"
      );
      loadHistory();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed to save");
    } finally {
      setSaving(false);
    }
  };

  const DAY_TYPES = [
    { v: "report", label: "Working day", icon: Send },
    { v: "off", label: "Off", icon: Coffee },
    { v: "leave", label: "Leave", icon: Plane },
  ];

  return (
    <div>
      <div className="mb-6">
        <h1 className="font-heading text-3xl sm:text-4xl font-bold tracking-tight">Your daily report</h1>
        <p className="text-muted-foreground mt-1">
          Share what you worked on and track your plans day to day.
        </p>
      </div>

      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList className="mb-6 max-w-full overflow-x-auto justify-start" data-testid="member-tabs">
          <TabsTrigger value="mom" data-testid="tab-mom">
            <ClipboardList className="h-4 w-4 mr-1.5" /> MOM
          </TabsTrigger>
          <TabsTrigger value="tickets" data-testid="tab-tickets">
            <Wrench className="h-4 w-4 mr-1.5" /> Facility & IT
          </TabsTrigger>
          <TabsTrigger value="mytodos" data-testid="tab-mytodos">
            <ClipboardList className="h-4 w-4 mr-1.5" /> To Do
          </TabsTrigger>
          <TabsTrigger value="leave" data-testid="tab-leave">
            <Plane className="h-4 w-4 mr-1.5" /> Leave / Off
          </TabsTrigger>
          <TabsTrigger value="submit" data-testid="tab-submit">
            <Send className="h-4 w-4 mr-1.5" /> Submit report
          </TabsTrigger>
          <TabsTrigger value="tracker" data-testid="tab-tracker">
            <ListChecks className="h-4 w-4 mr-1.5" /> Plan tracker
          </TabsTrigger>
          <TabsTrigger value="history" data-testid="tab-history">
            <History className="h-4 w-4 mr-1.5" /> My history
          </TabsTrigger>
          <TabsTrigger value="leaderboard" data-testid="tab-leaderboard">
            <Trophy className="h-4 w-4 mr-1.5" /> Leaderboard
          </TabsTrigger>
        </TabsList>

        <TabsContent value="mom">
          <MomView />
        </TabsContent>

        <TabsContent value="tickets">
          <TicketsView />
        </TabsContent>

        <TabsContent value="mytodos">
          <MyTodosView />
        </TabsContent>

        <TabsContent value="leave">
          <LeaveView />
        </TabsContent>

        <TabsContent value="leaderboard">
          <Leaderboard />
        </TabsContent>

        <TabsContent value="submit">
          <form onSubmit={submit} className="grid lg:grid-cols-3 gap-4 md:gap-6">
            <Card className="lg:col-span-2 p-5 sm:p-6 rounded-xl space-y-5">
              {status === "leave" ? (
                <div className="text-center py-10" data-testid="leave-placeholder">
                  <Plane className="h-10 w-10 mx-auto text-primary mb-3" />
                  <p className="font-heading font-semibold text-lg">Marked as Leave</p>
                  <p className="text-sm text-muted-foreground mt-1 max-w-sm mx-auto">
                    No report needed. This day won't be flagged as missing, but a leave day earns no
                    leaderboard points and breaks your reporting streak.
                  </p>
                </div>
              ) : (
                <>
                  {entryState === "draft" && status === "report" && (
                    <div className="rounded-lg bg-amber-500/10 text-amber-700 dark:text-amber-300 px-3 py-2 text-xs flex items-center gap-1.5" data-testid="draft-note">
                      <FileEdit className="h-3.5 w-3.5 shrink-0" />
                      This is a <strong>draft</strong> — only you can see it. It auto-saves as you type. Hit <strong>Submit report</strong> when you're done so your team can see it.
                    </div>
                  )}
                  {status === "off" && (
                    <div className="rounded-lg bg-blue-500/10 text-blue-700 dark:text-blue-300 px-3 py-2 text-xs" data-testid="off-note">
                      This is an <strong>Off</strong> day. Anything you add here is just for the record —
                      it will <strong>not</strong> count toward the leaderboard.
                    </div>
                  )}
                  <div className="space-y-2">
                    <Label htmlFor="summary">Overall summary</Label>
                    <Textarea id="summary" data-testid="report-summary-input" value={summary}
                      onChange={(e) => setSummary(e.target.value)} placeholder="A short summary of your day..." rows={4} />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-foreground">✅ Tasks accomplished</Label>
                    <ListEditor items={tasks} onChange={setTasks}
                      placeholder="Add a completed task and press Enter" testid="report-tasks" color="chart-2" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-primary">➡️ Plan for tomorrow</Label>
                    <PlanEditor items={plan} onChange={setPlan} />
                  </div>
                </>
              )}
            </Card>

            <div className="space-y-4 md:space-y-6">
              <Card className="p-5 sm:p-6 rounded-xl space-y-4">
                <div className="space-y-2">
                  <Label>Day type</Label>
                  <div className="grid grid-cols-3 gap-2" data-testid="day-type-selector">
                    {DAY_TYPES.map((t) => {
                      const Icon = t.icon;
                      const active = status === t.v;
                      return (
                        <button key={t.v} type="button" data-testid={`day-type-${t.v}`}
                          onClick={() => setStatus(t.v)}
                          className={`flex flex-col items-center gap-1 rounded-lg py-2.5 text-xs font-medium transition-colors border ${
                            active ? "bg-primary text-primary-foreground border-primary" : "bg-secondary hover:bg-accent border-transparent"
                          }`}>
                          <Icon className="h-4 w-4" /> {t.label}
                        </button>
                      );
                    })}
                  </div>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="date" className="flex items-center gap-1.5">
                    <CalendarDays className="h-4 w-4" /> Report date
                  </Label>
                  <Input id="date" type="date" min={yesterday()} max={today()} data-testid="report-date-input"
                    value={date} onChange={(e) => setDate(e.target.value)} />
                </div>
                {status === "report" && (
                  <div className="space-y-2">
                    <Label>Departments (optional)</Label>
                    <DeptPicker value={depts} onChange={setDepts} testid="report-dept-picker" />
                  </div>
                )}
                {status === "report" && (
                  <div className="space-y-2">
                    <Label>How was your day?</Label>
                    <div className="flex gap-2">
                      {moods.map((m) => (
                        <button key={m.v} type="button" data-testid={`report-mood-${m.v}`}
                          onClick={() => setMood(mood === m.v ? null : m.v)}
                          className={`h-10 w-10 rounded-lg text-lg transition-all ${
                            mood === m.v ? "bg-primary/15 ring-2 ring-primary scale-105" : "bg-secondary hover:bg-accent"
                          }`}>
                          {m.e}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                {status === "report" ? (
                  <div className="space-y-2">
                    <Button type="button" onClick={(e) => submit(e, false)} className="w-full rounded-lg h-11" data-testid="submit-report-btn" disabled={saving}>
                      {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (<><Send className="h-4 w-4 mr-1.5" /> Submit report</>)}
                    </Button>
                    <Button type="button" variant="outline" onClick={(e) => submit(e, true)} className="w-full rounded-lg h-11" data-testid="save-draft-btn" disabled={saving}>
                      <FileEdit className="h-4 w-4 mr-1.5" /> Save as draft
                    </Button>
                    <div className="h-4 text-center" data-testid="autosave-indicator">
                      {entryState !== "submitted" && autoState === "saving" && (
                        <span className="text-[11px] text-muted-foreground flex items-center justify-center gap-1"><Loader2 className="h-3 w-3 animate-spin" /> Auto-saving draft…</span>
                      )}
                      {entryState !== "submitted" && autoState === "saved" && (
                        <span className="text-[11px] text-emerald-600 flex items-center justify-center gap-1"><Check className="h-3 w-3" /> Draft auto-saved</span>
                      )}
                      {entryState === "submitted" && submittedAt && (
                        <span className="text-[11px] text-muted-foreground">Published {fmtTs(submittedAt)}</span>
                      )}
                    </div>
                  </div>
                ) : (
                  <Button type="button" onClick={(e) => submit(e, false)} className="w-full rounded-lg h-11" data-testid="submit-report-btn" disabled={saving}>
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : (
                      <>
                        {status === "off" ? <Coffee className="h-4 w-4 mr-1.5" /> : <Plane className="h-4 w-4 mr-1.5" />}
                        {status === "off" ? "Mark as Off" : "Mark as Leave"}
                      </>
                    )}
                  </Button>
                )}
                <p className="text-xs text-muted-foreground text-center">Drafts are private until you submit. Editing an existing date updates that entry. Off: 1–2 per week (Mon–Sun).</p>
              </Card>
            </div>
          </form>
        </TabsContent>

        <TabsContent value="tracker">
          <PlanTracker reports={reports} loading={loadingHistory} onChanged={loadHistory} />
        </TabsContent>

        <TabsContent value="history">
          <div className="flex flex-wrap items-end gap-3 mb-4">
            <div className="space-y-1"><Label className="text-xs">From</Label><Input type="date" value={histStart} max={histEnd || today()} onChange={(e) => setHistStart(e.target.value)} className="h-9 w-40" data-testid="history-start-input" /></div>
            <div className="space-y-1"><Label className="text-xs">To</Label><Input type="date" value={histEnd} max={today()} onChange={(e) => setHistEnd(e.target.value)} className="h-9 w-40" data-testid="history-end-input" /></div>
            {(histStart || histEnd) && <Button variant="ghost" size="sm" className="rounded-lg" onClick={() => { setHistStart(""); setHistEnd(""); }} data-testid="history-clear-range">Clear</Button>}
          </div>
          {loadingHistory ? (
            <div className="flex justify-center py-12"><Loader2 className="h-6 w-6 animate-spin text-primary" /></div>
          ) : (() => {
            const filtered = reports.filter((r) => (!histStart || r.date >= histStart) && (!histEnd || r.date <= histEnd));
            if (reports.length === 0) return (
              <Card className="p-12 text-center rounded-xl" data-testid="member-empty"><p className="text-muted-foreground">No reports yet. Submit your first one!</p></Card>
            );
            if (filtered.length === 0) return (
              <Card className="p-10 text-center rounded-xl" data-testid="member-history-empty-range"><p className="text-muted-foreground">No reports in this date range.</p></Card>
            );
            return (
              <div className="space-y-4" data-testid="member-history-list">
                {filtered.map((r, i) => (<ReportCard key={r.id} report={r} index={i} />))}
              </div>
            );
          })()}
        </TabsContent>
      </Tabs>
    </div>
  );
}
